import React, { useEffect, useState } from 'react';
import { Col, Container, Row } from 'react-bootstrap';
import { useParams } from 'react-router-dom';
import Authorize from '../../components/AuthComponent/Authorize';
import Button from '../../components/Buttons/Button';
import SaveButton from '../../components/Buttons/SaveButton';
import DataTable from '../../components/DataTable/DataTable';
import ButtonBar from '../../components/Fields/ButtonBar';
import Form from '../../components/Fields/Form';
import SelectListField from '../../components/Fields/SelectListField';
import SwitchField from '../../components/Fields/SwitchField';
import TextboxField from '../../components/Fields/TextboxField';
import { FormConfig } from '../../components/Fields/Types';
import Title from '../../components/Headers/Title';
import useSelectListOptions from '../../components/Hooks/SelectListOptionsHook/useSelectListOptions';
import useValidation from '../../components/Hooks/ValidationHook/useValidation';
import { ApplicationFunctions, useAuth } from '../../contexts/authContext';
import { useNotifications } from '../../contexts/notificationContext';
import DocumentTemplateModel from '../../models/DocumentTemplateModel';
import DocumentTemplateVersionModel from '../../models/DocumentTemplateVersionModel';
import DataService from '../../services/data.service';
import IdParams from '../../types/IdParams';
import { DataResult } from '../../types/Result';
import Unauthorized from '../Authentication/Unauthorized';
import DocumentTemplate_popup_ReviseTemplate from './Popups/DocumentTemplate_popup_ReviseTemplate';


const DocumentTemplateView: React.FC = () => {
    const attachmentTypes = useSelectListOptions('api/v1/selectlist/AttachmentTypes', '-- Select attachement type');
    const languages = useSelectListOptions('api/v1/selectlist/Languages', '-- Select language');
    const outputTypes = useSelectListOptions('api/v1/selectlist/DocumentOutputTypes', '-- Select output type');
    const [serverErrors, setServerErrors] = useState<DataResult<DocumentTemplateModel>>({ isSuccesful: true, messages: [], data: undefined });
    const { handleResult, handleException } = useNotifications();
    const [showPopup, setShowPopup] = useState<boolean>(false)
    const [isAuthorized, setIsAuthorized] = useState<boolean>(true);

    const [formData, setFormData] = useState<DocumentTemplateModel>({
        id: 0,
        name: '',
        attachmentTypeId: 0,
        language: '',
        documentOutputTypeId: 0,
        latestVersion: 1,
        isActive: true,
        emailSubject: '',
        concurrencyId: '',
    } as DocumentTemplateModel);

    let idParams: IdParams = useParams();
    let documentTemplateId: number = 0;
    if (idParams.id && !Number.isNaN(parseInt(idParams.id))) {
        documentTemplateId = parseInt(idParams.id);
    }

    useEffect(() => {
        getFormData();
    }, [documentTemplateId]);

    function getFormData() {
        if (documentTemplateId !== 0) {
            DataService.getById<DocumentTemplateModel>('api/v1/documenttemplates/', documentTemplateId)
                .then(response => setFormData(response))
                .catch(e => {
                    if (e.response?.data?.status === 401)
                        setIsAuthorized(false);
                    else
                        handleException(e);
                });
        }
    }

    function submit() {
        if (formData) {
            DataService
                .update<DocumentTemplateModel>(`api/v1/documenttemplates/`, formData)
                .then(response => {
                    handleResult(response);
                    setServerErrors(response);
                    if (response.isSuccesful === true && response.data)
                        setFormData(response.data);
                })
                .catch(e => handleException(e));
        }
    }

    function downloadDocumentTemplateVersion(rowData: DocumentTemplateVersionModel) {
        if (rowData.fileUrl) {
            const link = document.createElement('a');
            link.href = rowData.fileUrl;
            link.target = '_blank';
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
        }
    }

    const formConfig: FormConfig = {
        fields: [
            { name: 'name', label: 'Name', validationRules: [{ type: 'isRequired' }] },
            { name: 'emailSubject', label: 'Email subject' },
            { name: 'attachmentTypeId', label: 'Attachment type', validationRules: [{ type: 'isRequired' }] },
            { name: 'language', label: 'Language', validationRules: [{ type: 'isRequired' }] },
            { name: 'documentOutputTypeId', label: 'Output type', validationRules: [{ type: 'isRequired' }] },
            { name: 'isActive', label: 'Is active' },
        ],
        handleSubmit: submit,
        showErrors: 'always',
    };

    const { getFieldProps, getSubmitButtonProps } = useValidation(formConfig, formData, serverErrors);

    const { isAuthorizedFor } = useAuth();
    if (documentTemplateId === 0) {
        if (!isAuthorizedFor(ApplicationFunctions.EditDocumentTemplates)) {
            return (<Unauthorized />);
        }
    }
    else if (!isAuthorized) {
        return (<Unauthorized />);
    }

    return (
        <div className='main-content'>
            <Form title='Document template' >
                <Container fluid>
                    <Row>
                        <Col md='12'>
                            <TextboxField {...getFieldProps('name')} />
                            <TextboxField {...getFieldProps('emailSubject')} />
                            <SelectListField {...getFieldProps('attachmentTypeId')} options={attachmentTypes} />
                            <SelectListField {...getFieldProps('language')} options={languages} />
                            <SelectListField {...getFieldProps('documentOutputTypeId')} options={outputTypes} />
                            <SwitchField {...getFieldProps('isActive')} />
                            <ButtonBar offset={2}>
                                <Authorize applicationFunction={ApplicationFunctions.EditDocumentTemplates}>
                                    <SaveButton  {...getSubmitButtonProps()} />
                                    <Button type='button' text='Revise' onClick={() => setShowPopup(true)} />

                                </Authorize>
                            </ButtonBar>
                            <Title text="Versions" size={5} />
                            <DataTable
                                name='dtDocumentTemplateVersions'
                                colInfos={[
                                    { key: true, dataProp: 'id', visible: false },
                                    { title: 'Version', dataProp: 'versionNumber', sort: 'desc', dataType: 'number', width: 100 },
                                    { title: 'Startdate', dataProp: 'BeginDate', dataType: 'datetime' },
                                    { title: 'Enddate', dataProp: 'EndDate', dataType: 'datetime' },
                                    { title: 'Created on', dataProp: 'createdOn', dataType: 'datetime' },
                                    { title: 'Created by', dataProp: 'createdByUser', dataType: 'user', width: 'auto' },
                                    { rowButtons: [{ text: "Download", icon: 'fa-cloud-download', color: '#89C4F4', onClick: downloadDocumentTemplateVersion }] },
                                ]}
                                tableData={formData.documentTemplateVersions}
                            />
                        </Col>
                    </Row>
                </Container>
            </Form>
            {showPopup && <DocumentTemplate_popup_ReviseTemplate documentTemplateId={formData.id} show={showPopup} onHide={() => { setShowPopup(false); getFormData(); }} />}
        </div>
    );
}
export default DocumentTemplateView;